import { FC, useState, useMemo, useEffect } from 'react';
import { useStore } from 'hooks/useRootStore';
import { useQuery, useMutation } from 'react-query';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { observer } from 'mobx-react-lite';
import { GridColumns, DataGrid, GridCellParams, GridSelectionModelChangeParams } from '@material-ui/data-grid';
import IconButton from '@material-ui/core/IconButton';
import PersonIcon from '@material-ui/icons/Person';
import SyncIcon from '@mui/icons-material/Sync';
import SyncDisabledIcon from '@material-ui/icons/SyncDisabled';
import { useStyles } from './styles';
import { Paper, Tooltip, Button } from '@material-ui/core';
import EmailIcon from '../../../assets/icons/EndUser/Email.svg';
import { toFormatDateTime } from 'utils';
import { green, red } from '@material-ui/core/colors';
import { getRowsPerPageOptions } from 'utils/pagination';

export const GithubSyncUser: FC = observer(() => {
  const classes = useStyles();
  const [selectedEmail, setSelectedEmail] = useState<string[]>([]);
  const {
    GithubStore: { syncUserGitHub, allsyncUsersView, reSyncSingleUser, SelectedUser, getOrganisation },
    dashboardStore: { generalStatistic, getStatistic },
    ldapStore: { usersView, getUsers },
    notificationStore: { openNotification },
  } = useStore();
  const getOrganisationReq = useQuery('getOrganisationReq', () => getOrganisation(), {
    onError: () => openNotification('error', `We are unable to get the organisation from github`),
  });

  const syncGithubusersReq = useQuery(
    ['syncUser', getOrganisationReq?.data?.organisation],
    () => {
      const organisation = getOrganisationReq?.data?.organisation;
      return organisation ? syncUserGitHub(organisation) : Promise.resolve([]);
    },
    {
      enabled: !!getOrganisationReq?.data?.organisation,
      onError: () => openNotification('error', `We are unable to get the user from GitHub`),
    }
  );

  const getUsersReq = useQuery('getUsersReq', () => getUsers(), {
    onError: () => openNotification('error', `Can't get users`),
  });
  const getStatisticReq = useQuery('getStatisticReq', () => getStatistic(), {
    onError: () => openNotification('error', `Can't get statistic`),
  });

  const reSyncSingleUserReq = useMutation((data: { githubId: string }) => reSyncSingleUser(data), {
    mutationKey: 'reSyncSingleUserReq',
    onSuccess: (_, variables) => {
      openNotification('success', 'Selected Users sync’ed with App Google Successfully');
      setSyncedUsers((prev) => new Set(prev).add(variables.githubId));
      syncGithubusersReq.refetch();
      getUsersReq.refetch();
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to sync user. Please ensure the GitHub profile has complete information including first and last name.';
      openNotification('error', errorMessage);
    },
  });

  const selectedUserReq = useMutation((data: { githubIds: string[] }) => SelectedUser(data), {
    mutationKey: 'selectedUserReq',
    onSuccess: () => {
      openNotification('success', 'Selected Users synced with App Google Successfully');
      setSelectedEmail([]);
      syncGithubusersReq.refetch();
      getUsersReq.refetch();
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'An unknown error occurred';
      openNotification('error', errorMessage);
    },
  });

  useEffect(() => {
    document.title = 'EasyIdentity: App: SyncUser';
  }, []);

  const user = usersView.map((item: { githubId: any }) => item.githubId);
  const filteredUserSet = new Set(user);
  const [syncedUsers, setSyncedUsers] = useState<Set<string>>(new Set());
  const rows = useMemo(
    () => (!syncGithubusersReq.isFetching ? allsyncUsersView : []),
    [syncGithubusersReq.isFetching, allsyncUsersView]
  );
  const [pageSize, setPageSize] = useState(10);
  const rowsPerPageOptions = useMemo(() => getRowsPerPageOptions(rows), [rows]);
  const handleSync = (githubId: string, name?: string, email?: string) => {
    if (!name || !email) {
      openNotification('error', 'Please update GitHub profile with complete name and email before syncing');
      return;
    }
    const nameParts = name.trim().split(' ');
    if (nameParts.length < 2) {
      openNotification('error', 'GitHub profile must have both first and last name for syncing');
      return;
    }

    reSyncSingleUserReq.mutate({ githubId });
  };

  const columns: GridColumns = [
    {
      field: 'icon',
      headerName: ' ',
      width: 40,
      align: 'center',
      disableColumnMenu: true,
      renderCell: () => <PersonIcon style={{ color: 'grey' }} />,
    },
    {
      field: 'created_at',
      headerName: 'CREATED',
      flex: 0.9,
      renderCell: (params: GridCellParams) => (
        <Tooltip title={toFormatDateTime(params.row.created_at)} placement='bottom'>
          <div style={{ display: 'flex', gap: '10px' }}>{toFormatDateTime(params.row.created_at)}</div>
        </Tooltip>
      ),
    },
    {
      field: 'updated_at',
      headerName: 'UPDATED',
      flex: 0.9,
      renderCell: (params: GridCellParams) => (
        <Tooltip title={toFormatDateTime(params.row.updated_at)} placement='bottom'>
          <div style={{ display: 'flex', gap: '10px' }}>{toFormatDateTime(params.row.updated_at)}</div>
        </Tooltip>
      ),
    },
    {
      field: 'login',
      headerName: 'USERNAME',
      flex: 0.8,
      renderCell: (params: GridCellParams) => (
        <Tooltip title={params.row.login || ''}>
          <div>
            <span>{params.row.login}</span>
          </div>
        </Tooltip>
      ),
    },
    {
      field: 'name',
      headerName: 'FULL NAME',
      flex: 0.8,
      renderCell: (params: GridCellParams) => (
        <Tooltip title={params.row.name || ''}>
          <div>
            <span>{params.row.name}</span>
          </div>
        </Tooltip>
      ),
    },
    {
      field: 'email',
      headerName: 'EMAIL',
      flex: 1,
      renderCell: (params: GridCellParams) => {
        const value = String(params.value ?? '');

        return (
          <Tooltip title={value} placement='bottom'>
            <div style={{ display: 'flex', gap: '10px' }}>
              <img src={EmailIcon} alt='' />
              <span>{value}</span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'ACTIONS',
      width: 160,
      disableColumnMenu: true,
      renderCell: (params: GridCellParams) => {
        const { githubId, name, email } = params.row;
        const isSynced = filteredUserSet.has(githubId) || syncedUsers.has(githubId);

        return (
          <Tooltip title={isSynced ? 'Sync' : 'Not Sync'}>
            <IconButton size='small' onClick={() => handleSync(githubId, name, email)}>
              {isSynced ? <SyncIcon style={{ color: green[500] }} /> : <SyncDisabledIcon style={{ color: red[500] }} />}
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  const ldapUserCount = usersView.length ?? 0;
  const githubUserCount = allsyncUsersView.length ?? 0;

  const disabledSyncCount = rows
    .filter((row) => row.githubId !== null)
    .filter((row) => row.githubId && !filteredUserSet.has(row.githubId) && !syncedUsers.has(row.githubId)).length;

  useAppPreloader(
    syncGithubusersReq.isFetching ||
      getUsersReq.isFetching ||
      reSyncSingleUserReq.isLoading ||
      getStatisticReq.isFetching
  );

  const handleSelectionModelChange = (params: GridSelectionModelChangeParams) => {
    const selectedIds = params.selectionModel as number[];
    const selectedData = rows.filter((row: any) => selectedIds.includes(row.id));
    const githubIds = selectedData
      .map((item: any) => item.githubId)
      .filter((githubId: any): githubId is string => githubId !== undefined);
    setSelectedEmail(githubIds);
  };

  return (
    <>
      {!syncGithubusersReq.isFetching && !getUsersReq.isFetching && !getStatisticReq.isFetching && (
        <Paper className={classes.dataGrid1} elevation={0}>
          <h1 className={classes.textBox}>You have {ldapUserCount} Users in EasyIdentity.</h1>
          <h1 className={classes.textBox}>You have {githubUserCount} Users in Github Workspace.</h1>
          <h1 className={classes.textBox}>Sync Users : EasyIdentity {'<->'} Github Workspace</h1>
          {disabledSyncCount > Number(generalStatistic?.leftAccounts) && (
            <h1 className={classes.textBox}>
              There are only {generalStatistic?.leftAccounts || '0'} Easyidentity User Licenses remaining. Please
              upgrade your subscription to Sync Easyidentity User Accounts with Github Workspace.
            </h1>
          )}
        </Paper>
      )}

      <Paper className={classes.dataGrid1} elevation={0}>
        <h1 style={{ fontWeight: '400', fontSize: '120%', textAlign: 'center' }}>List of All Github Workspace Users</h1>
      </Paper>

      <Paper className={classes.dataGrid} elevation={0}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            justifyContent: 'flex-end',
            marginBottom: '10px',
          }}
        >
          <Button
            onClick={() => {
              const selectedData = rows.filter((row: any) => selectedEmail.includes(row.githubId));
              const invalidUsers = selectedData.filter((user) => !user.name || !user.email);

              if (invalidUsers.length > 0) {
                openNotification(
                  'error',
                  'Some selected users are missing name or email. Please update their GitHub profile before syncing.'
                );
                return;
              }

              selectedUserReq.mutate({ githubIds: selectedEmail });
            }}
            variant='contained'
            color='primary'
            disabled={selectedEmail.length === 0}
          >
            Sync
          </Button>
        </div>
      </Paper>

      <Paper className={classes.dataGrid} elevation={0}>
        <DataGrid
          className={classes.noBorder}
          autoHeight
          rows={rows}
          columns={columns}
          pageSize={pageSize}
          rowsPerPageOptions={rowsPerPageOptions}
          pagination
          onPageChange={(params) => setPageSize(params.pageSize)}
          checkboxSelection
          onSelectionModelChange={handleSelectionModelChange}
        />
      </Paper>
    </>
  );
});
